/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { isMenuFilterActive } from "utils/menu_helper"
import { useMenuOverviewDepartments } from "./useMenuOverviewDepartments"
import { useMenuOverviewSubDepartments } from "./useMenuOverviewSubDepartments"

/**
 * Custom hook that handles auto-selection of departments and subdepartments
 * when the current selection is not in the filtered results.
 *
 * This is only needed when enablePartialMenu=true AND filters are active,
 * because RTK Query bypasses the Redux handler auto-selection logic.
 * When no filters are active, V3 components handle their own auto-selection.
 */
export const useMenuAutoSelection = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    overview: {
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      sellingTime,
    },
    isMartDepartmentStructure,
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  // Check if filters are active - only run auto-selection when using legacy components
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Get filtered data from hooks
  const {
    filteredDepartments,
    isLoading: isDepartmentsLoading,
  } = useMenuOverviewDepartments()
  const {
    filteredSubDepartments,
    isLoading: isSubDepartmentsLoading,
  } = useMenuOverviewSubDepartments()

  // Auto-select first department when current selection is not in filtered results
  // Only runs when enablePartialMenu=true AND filters are active (legacy components)
  useEffect(() => {
    if (
      enablePartialMenu &&
      isFilterActive &&
      isMartDepartmentStructure &&
      !isDepartmentsLoading &&
      filteredDepartments.length > 0
    ) {
      const previouslySelected = selectedDepartment
        ? filteredDepartments.find(
            dept => dept.itemClassID === selectedDepartment.itemClassID,
          )
        : null

      if (!previouslySelected) {
        dispatch(actionCreator.menu.selectDepartment(filteredDepartments[0]))
      }
    }
  }, [
    enablePartialMenu,
    isFilterActive,
    isMartDepartmentStructure,
    isDepartmentsLoading,
    filteredDepartments,
    selectedDepartment,
    dispatch,
  ])

  // Auto-select first subdepartment when current selection is not in filtered results
  // Only runs when enablePartialMenu=true AND filters are active (legacy components)
  useEffect(() => {
    if (
      enablePartialMenu &&
      isFilterActive &&
      isMartDepartmentStructure &&
      selectedDepartment?.itemClassID &&
      !isSubDepartmentsLoading &&
      filteredSubDepartments.length > 0
    ) {
      const previouslySelected = selectedSubDepartment
        ? filteredSubDepartments.find(
            sd => sd.itemClassID === selectedSubDepartment.itemClassID,
          )
        : null

      if (!previouslySelected) {
        dispatch(
          actionCreator.menu.selectSubDepartment(filteredSubDepartments[0]),
        )
      }
    }
  }, [
    enablePartialMenu,
    isFilterActive,
    isMartDepartmentStructure,
    isSubDepartmentsLoading,
    filteredSubDepartments,
    selectedSubDepartment,
    selectedDepartment,
    dispatch,
  ])
}
